// Privacy Policy content in English and Spanish
import type { LocalizedLegalContent } from './types';

export const privacyPolicy: LocalizedLegalContent = {
  en: {
    title: 'StartUs Privacy Policy',
    lastUpdated: 'April 16, 2026',
    introParagraphs: [
      'StartUs ("Company", "we", "us", or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the StartUs platform ("Platform", "Service"). By using the Service, you agree to the practices described in this policy.',
    ],
    sections: [
      {
        title: '1. Information We Collect',
        paragraphs: ['a. Information You Provide'],
        items: [
          'Name, email address, and account credentials',
          'Startup information (company name, industry, team data)',
          'Uploaded files (pitch decks, videos, documents)',
          'Communications with us',
        ],
      },
      {
        title: '1b. Automatically Collected Information',
        items: [
          'IP address',
          'Browser type and device information',
          'Usage data and interaction logs',
          'Cookies and tracking technologies',
        ],
      },
      {
        title: '2. How We Use Your Information',
        paragraphs: ['We use your information to:'],
        items: [
          'Provide and operate the Platform',
          'Process and analyze pitch content using AI',
          'Generate application responses',
          'Improve our services and AI models',
          'Communicate updates and support',
          'Ensure platform security',
        ],
      },
      {
        title: '3. AI Processing',
        paragraphs: [
          'StartUs uses artificial intelligence to analyze uploaded content and generate suggestions.',
          'By uploading content, you consent to automated processing of your data and AI-generated outputs based on your inputs.',
          'We do not use your confidential content for public training of external AI models without explicit consent.',
        ],
      },
      {
        title: '4. Data Sharing',
        paragraphs: ['We do not sell your personal data.', 'We may share information with:'],
        items: [
          'Service providers (hosting, analytics, infrastructure)',
          'Legal authorities if required by law',
          'Business successors in case of merger or acquisition',
        ],
      },
      {
        title: '5. Data Retention',
        paragraphs: [
          'We retain your information as long as your account is active or as necessary to comply with legal obligations.',
          'You may request deletion of your account at any time.',
        ],
      },
      {
        title: '6. Data Security',
        paragraphs: [
          'We implement reasonable technical and organizational safeguards to protect your data. However, no system is 100% secure.',
        ],
      },
      {
        title: '7. Your Rights',
        paragraphs: ['Depending on your jurisdiction, you may have the right to:'],
        items: [
          'Access your personal data',
          'Correct inaccurate data',
          'Request deletion',
          'Object to certain processing',
          'Withdraw consent',
        ],
        trailing: 'To exercise these rights, contact: soporte@spaziocreative.com',
      },
      {
        title: '8. Cookies',
        paragraphs: [
          'We may use cookies and similar technologies to improve user experience and analyze platform usage.',
          'You can manage cookie preferences in your browser settings.',
        ],
      },
      {
        title: '9. International Data Transfers',
        paragraphs: [
          'Your information may be processed in countries outside your residence, subject to appropriate safeguards.',
        ],
      },
      {
        title: '10. Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy periodically. Continued use of the Service constitutes acceptance of updates.',
        ],
      },
      {
        title: '11. Contact',
        paragraphs: [
          'If you have questions about this Privacy Policy, contact us at: soporte@spaziocreative.com',
        ],
      },
    ],
  },
  es: {
    title: 'Política de Privacidad de StartUs',
    lastUpdated: '16 de abril de 2026',
    introParagraphs: [
      'StartUs ("Compañía", "nosotros") respeta tu privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos y protegemos tu información cuando utilizas la plataforma StartUs. Al usar el Servicio, aceptas las prácticas descritas en esta política.',
    ],
    sections: [
      {
        title: '1. Información que Recopilamos',
        paragraphs: ['a. Información que proporcionas'],
        items: [
          'Nombre, correo electrónico y credenciales',
          'Información de la startup (nombre, industria, equipo)',
          'Archivos subidos (pitch, videos, documentos)',
          'Comunicaciones con nosotros',
        ],
      },
      {
        title: '1b. Información recopilada automáticamente',
        items: [
          'Dirección IP',
          'Tipo de navegador y dispositivo',
          'Datos de uso y actividad',
          'Cookies y tecnologías similares',
        ],
      },
      {
        title: '2. Cómo Usamos la Información',
        paragraphs: ['Utilizamos la información para:'],
        items: [
          'Operar la Plataforma',
          'Analizar contenido mediante IA',
          'Generar respuestas automatizadas',
          'Mejorar nuestros servicios',
          'Garantizar seguridad',
          'Brindar soporte',
        ],
      },
      {
        title: '3. Procesamiento con IA',
        paragraphs: [
          'StartUs utiliza inteligencia artificial para analizar contenido y generar sugerencias.',
          'Al subir contenido, aceptas el procesamiento automatizado de tus datos y la generación de resultados basados en tus entradas.',
          'No utilizamos contenido confidencial para entrenar modelos públicos sin consentimiento explícito.',
        ],
      },
      {
        title: '4. Compartición de Datos',
        paragraphs: ['No vendemos datos personales.', 'Podemos compartir información con:'],
        items: [
          'Proveedores de servicios tecnológicos',
          'Autoridades cuando lo exija la ley',
          'En caso de fusión o adquisición empresarial',
        ],
      },
      {
        title: '5. Retención de Datos',
        paragraphs: [
          'Conservamos la información mientras la cuenta esté activa o sea necesario por obligaciones legales.',
          'Puedes solicitar la eliminación de tu cuenta en cualquier momento.',
        ],
      },
      {
        title: '6. Seguridad',
        paragraphs: [
          'Implementamos medidas técnicas razonables para proteger los datos. Sin embargo, ningún sistema es completamente seguro.',
        ],
      },
      {
        title: '7. Derechos del Usuario',
        paragraphs: ['Dependiendo de tu jurisdicción, puedes tener derecho a:'],
        items: [
          'Acceder a tus datos',
          'Corregir información inexacta',
          'Solicitar eliminación',
          'Oponerte al procesamiento',
          'Retirar consentimiento',
        ],
        trailing: 'Para ejercer estos derechos, contáctanos en: soporte@spaziocreative.com',
      },
      {
        title: '8. Cookies',
        paragraphs: [
          'Podemos usar cookies para mejorar la experiencia y analizar el uso.',
          'Puedes gestionarlas desde la configuración de tu navegador.',
        ],
      },
      {
        title: '9. Transferencias Internacionales',
        paragraphs: [
          'Tu información puede procesarse en países distintos al tuyo, bajo medidas de protección adecuadas.',
        ],
      },
      {
        title: '10. Cambios a la Política',
        paragraphs: [
          'Podemos actualizar esta Política periódicamente. El uso continuo implica aceptación.',
        ],
      },
      {
        title: '11. Contacto',
        paragraphs: [
          'Si tienes preguntas sobre esta Política de Privacidad, contáctanos en: soporte@spaziocreative.com',
        ],
      },
    ],
  },
};
